// 后端接口封装 —— 任务提交 + 轮询。函数名保留 stream，是为了兼容旧调用方。
import { authenticatedFetch } from "../../../utils/api";
import { API_HOST } from "../../../utils/constants";
import { queryTaskStatus, runTaskToCompletion, submitTask, unwrapTaskResult } from "../../../services/taskService";
import { getCurrentImageProvider } from '../../../utils/wanxImageCapabilities';
import { normalizeTextProvider, resolveDatastartWorkflowTextProvider } from '../../../constants/globalAiProviders';
import { isDatastartImageProvider, normalizeDatastartImageTaskPayload } from '../../../constants/datastartImageModels';
import { isAstraflowImageProvider } from '../../../constants/astraflowImageModels';
import { DREAMFLY_LLM_MODEL_STORAGE_KEY, DREAMFLY_LLM_PROVIDER_ID, isDreamflyImageApiModel } from '../../../constants/dreamflyModels';

export const resolveStoryboardBoardRequest = (payload) => {
  const imageProvider = getCurrentImageProvider();
  if (!isDatastartImageProvider(imageProvider) && !isAstraflowImageProvider(imageProvider)) {
    const provider = localStorage.getItem('ai_provider') || 'huobao_gpt';
    return { ...payload, provider };
  }
  const textFields = {};
  if (payload?.segmentationMode !== 'manual') {
    textFields.textProvider = isDatastartImageProvider(imageProvider)
      ? resolveDatastartWorkflowTextProvider(localStorage.getItem('ai_provider'))
      : normalizeTextProvider(localStorage.getItem('ai_provider'));
    if (textFields.textProvider === DREAMFLY_LLM_PROVIDER_ID) {
      const selectedTextModel = String(localStorage.getItem(DREAMFLY_LLM_MODEL_STORAGE_KEY) || '').trim();
      if (!selectedTextModel || isDreamflyImageApiModel(selectedTextModel)) {
        throw new Error('请先选择 Dreamfly 文字子模型，再生成分镜画板');
      }
      textFields.textModel = selectedTextModel;
    }
  }
  return normalizeDatastartImageTaskPayload({
    ...payload,
    provider: imageProvider,
    ...textFields,
  }, imageProvider, { normalizeAspectRatio: false });
};

/**
 * 解析单个 SSE 帧（event:xxx + data:xxx 块）
 */
export const parseSseBlock = (block) => {
  const lines = block.split(/\r?\n/);
  let event = "message";
  const dataLines = [];
  lines.forEach((line) => {
    if (line.startsWith("event:")) {
      event = line.slice(6).trim() || "message";
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trimStart());
    }
  });
  if (!dataLines.length) return null;
  const rawData = dataLines.join("\n");
  try {
    return { event, data: JSON.parse(rawData) };
  } catch {
    return { event, data: rawData };
  }
};

/**
 * POST /api/task/submit
 * 后端以持久化任务执行故事板生成，前端轮询任务状态。
 *
 * handlers:
 *   - start(info)       首次建立连接
 *   - board(boardDto)   每张完成推送一张
 *   - done(summary)     全部结束
 *   - 其他 event.key    可按需扩展
 *
 * 旧 SSE 路径保留在后端作为回滚入口，但主流程不再依赖长连接。
 */
const emitStoryboardBoardResult = (result, handlers = {}) => {
  const boards = Array.isArray(result?.boards) ? result.boards : [];
  boards.forEach((board) => handlers.board?.(board));
  const summary = {
    ...(result || {}),
    count: Number(result?.count) || boards.length,
    successCount: Number(result?.successCount) || boards.filter((board) => board?.status === "success").length,
    failedCount: Number(result?.failedCount) || boards.filter((board) => board?.status === "failed").length,
  };
  handlers.done?.(summary);
  return summary;
};

export const pollStoryboardBoardTask = async (taskId, handlers = {}, options = {}) => {
  if (!taskId) throw new Error("缺少故事板生成任务ID");
  const startedAt = Date.now();
  const timeoutMs = options.timeoutMs || 60 * 60 * 1000;
  const initialIntervalMs = options.intervalMs || 5000;

  return await new Promise((resolve, reject) => {
    let stopped = false;
    let timer = null;
    const stop = () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      options.signal?.removeEventListener?.("abort", onAbort);
    };
    const makeAbortError = () => {
      const error = new Error("故事板任务轮询已取消");
      error.name = "AbortError";
      return error;
    };
    const onAbort = () => {
      if (stopped) return;
      stop();
      reject(makeAbortError());
    };
    const poll = async () => {
      if (stopped) return;
      if (options.signal?.aborted) {
        onAbort();
        return;
      }
      if (Date.now() - startedAt > timeoutMs) {
        stop();
        const error = new Error(`故事板任务仍在后台执行，前端本轮等待已超时：${taskId}`);
        error.recoverable = true;
        error.taskId = taskId;
        error.taskStatus = "processing";
        reject(error);
        return;
      }
      try {
        const status = await queryTaskStatus(taskId);
        if (stopped) return;
        options.onProgress?.(status);
        handlers.progress?.(status);
        if (status.status === "success") {
          stop();
          const result = unwrapTaskResult(status.result);
          const summary = emitStoryboardBoardResult(result, handlers);
          resolve({ taskId, result, rawResult: status.result, summary });
          return;
        }
        if (["failed", "not_found", "canceled", "cancelled"].includes(status.status)) {
          stop();
          const error = new Error(status.errorMessage || `故事板任务${status.status || "失败"}：${taskId}`);
          error.terminal = true;
          error.taskId = taskId;
          error.taskStatus = status.status;
          reject(error);
          return;
        }
        const elapsed = Date.now() - startedAt;
        const interval = elapsed < 30000 ? initialIntervalMs : elapsed < 120000 ? 8000 : 15000;
        timer = setTimeout(poll, interval);
      } catch {
        if (!stopped) timer = setTimeout(poll, 8000);
      }
    };

    if (options.signal?.aborted) {
      onAbort();
      return;
    }
    options.signal?.addEventListener?.("abort", onAbort, { once: true });
    poll();
  });
};

export const postStoryboardBoardStream = async (payload, handlers = {}, options = {}) => {
  const submittedPayload = resolveStoryboardBoardRequest(payload);
  const provider = submittedPayload.provider;
  handlers.start?.({
    count: submittedPayload.count,
    durationSecondsPerBoard: submittedPayload.durationSecondsPerBoard,
    provider,
  });
  const task = await submitTask("EPISODE_STORYBOARD_BOARD_GENERATE", submittedPayload, {
    provider,
    projectId: submittedPayload.projectId,
    episodeId: submittedPayload.episodeId,
    source: "episode_storyboard_board_generate",
  });
  options.onSubmitted?.(task);
  handlers.start?.({
    count: submittedPayload.count,
    durationSecondsPerBoard: submittedPayload.durationSecondsPerBoard,
    provider,
    taskId: task.taskId,
    status: task.status,
  });
  return pollStoryboardBoardTask(task.taskId, handlers, {
    timeoutMs: options.timeoutMs || 60 * 60 * 1000,
    intervalMs: 5000,
    signal: options.signal,
    onProgress: options.onProgress,
  });
};

/**
 * 单张重生成也走持久化任务，避免浏览器断开导致结果丢失。
 */
export const regenerateStoryboardBoard = async (payload) => {
  const submittedPayload = resolveStoryboardBoardRequest(payload);
  const provider = submittedPayload.provider;
  const { result } = await runTaskToCompletion("EPISODE_STORYBOARD_BOARD_REGENERATE", submittedPayload, {
    provider,
    projectId: submittedPayload.projectId,
    episodeId: submittedPayload.episodeId,
    source: "episode_storyboard_board_regenerate",
    timeoutMs: 60 * 60 * 1000,
    intervalMs: 5000,
  });
  return result;
};

export const loadStoryboardBoardState = async (projectId, episodeId) => (
  authenticatedFetch(`${API_HOST}/api/episode-storyboard-board/state?projectId=${projectId}&episodeId=${episodeId}`)
);

export const saveStoryboardBoardState = async (projectId, episodeId, state) => (
  authenticatedFetch(`${API_HOST}/api/episode-storyboard-board/state`, {
    method: "POST",
    body: JSON.stringify({
      ...(state || {}),
      projectId: Number(projectId),
      episodeId: Number(episodeId),
    }),
  })
);

export const loadStoryboardBoardVideos = async (projectId, episodeId) => (
  authenticatedFetch(`${API_HOST}/api/episode-storyboard-board/videos?projectId=${projectId}&episodeId=${episodeId}`)
);

export const updateStoryboardBoard = async (projectId, episodeId, boardIndex, board) => (
  authenticatedFetch(`${API_HOST}/api/episode-storyboard-board/board`, {
    method: "POST",
    body: JSON.stringify({
      ...(board || {}),
      projectId: Number(projectId),
      episodeId: Number(episodeId),
      boardIndex: Number(boardIndex),
    }),
  })
);

export const updateStoryboardBoardVideo = async (projectId, episodeId, boardIndex, video) => (
  authenticatedFetch(`${API_HOST}/api/episode-storyboard-board/video`, {
    method: "POST",
    body: JSON.stringify({
      ...(video || {}),
      projectId: Number(projectId),
      episodeId: Number(episodeId),
      boardIndex: Number(boardIndex),
    }),
  })
);
